import { Inline, Stack } from '@bedrock-layout/primitives';
import { zodResolver } from '@hookform/resolvers/zod';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { Button, Link, Spinner, TextField } from 'ui';
import { z } from 'zod';

import { SignupFormContainer } from '../../../components/authentication/layout/AuthPagesLayout.styled';
import { useSendEventToGTM } from '../../../hooks/useSendEventToGTM';
import {
  generateCaptchaToken,
  getPublicIP,
  getTimeZone,
} from '../../../utils/common';
import { emailValidationSchema } from '../../../utils/schema';
import { useSignin } from './useSignin';

const FormValidationSchema = z.object({
  ...emailValidationSchema,
  password: z.string().min(8, 'Password must contain at least 8 characters'),
});

type SigninFormValues = {
  email: string;
  password: string;
};

export const SigninForm = () => {
  const [togglePassword, setTogglePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { signin, isLoading: isApiLoading } = useSignin({ setIsLoading });
  const showSpinner = isLoading || isApiLoading;

  const { sendEventToGTM } = useSendEventToGTM();

  const { control, handleSubmit } = useForm<SigninFormValues>({
    resolver: zodResolver(FormValidationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  useEffect(() => {
    sendEventToGTM({
      event: 'page_view',
      page_path: window.location.pathname,
      page_title: 'Signin',
      page_location: window.location.href,
    });

    const source: Record<string, string> = {
      ...JSON.parse(window.localStorage.getItem('source') ?? '{}'),
      ...JSON.parse(Cookies.get('nected_source') ?? '{}'),
    };
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    for (const [key, value] of params.entries()) {
      if (key.includes('utm_')) {
        source[key] = value;
      }
    }

    source.utm_timeZone = getTimeZone();

    window.localStorage.setItem('source', JSON.stringify(source));
    void getPublicIP();
  }, []);

  const onSubmit = ({ email, password }: SigninFormValues) => {
    generateCaptchaToken(signin, { email, password });
  };

  return (
    <SignupFormContainer onSubmit={handleSubmit(onSubmit)}>
      <Stack gutter="1.8rem">
        <Stack gutter=".8rem">
          <Stack gutter="1.6rem">
            <TextField name="email" control={control} label="Email" />

            <TextField
              onClickInputIcon={() => setTogglePassword((isShow) => !isShow)}
              type={togglePassword ? 'text' : 'password'}
              name="password"
              icon={
                togglePassword ? (
                  <AiOutlineEye size={20} />
                ) : (
                  <AiOutlineEyeInvisible size={20} />
                )
              }
              control={control}
              label="Password"
            />
          </Stack>

          <Inline justify="end">
            <Link to="/wl-forgot-password">Forgot Password</Link>
          </Inline>
        </Stack>

        <Stack gutter="0.5rem">
          <Button type="submit" size="large" disabled={showSpinner}>
            {showSpinner ? <Spinner size="extraSmall" /> : 'Sign In'}
          </Button>
        </Stack>
      </Stack>
    </SignupFormContainer>
  );
};
